import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel as ChakraFormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  SimpleGrid,
  Text,
  Flex,
  Badge,
  useColorModeValue,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { useSetRecoilState } from "recoil";
import useAxios from "axios-hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import {
  PAYOUT_CONFIG_FIELDS,
  PAYOUT_CONFIG_DEFAULTS,
  payoutConfigValidationSchema,
  payoutConfigFormValues,
  buildPayoutConfigPayload,
  formatPayoutConfigInputValue,
} from "../variables/payoutConfigFields";

const FormLabel = styled(ChakraFormLabel)`
  color: #4a5568;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 6px;
  display: inline-block;
`;

const StyledInput = styled(Input)`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(245, 125, 54, 0.2);
  border-radius: 8px;
  height: 40px;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(245, 125, 54, 0.3);
  }

  &:focus {
    background: rgba(255, 255, 255, 0.9);
    border-color: #f57d36;
    box-shadow: 0 0 0 1px rgba(245, 125, 54, 0.3);
  }

  &::placeholder {
    color: rgba(160, 174, 192, 0.7);
  }
`;

// Modal for creating / editing a charger's payout configuration.
// Reuses the shared field definitions + yup schema so it stays in sync with
// the per-station Configurations.jsx component.
const PayoutConfigModal = ({
  isOpen,
  onClose,
  station,
  existingConfig,
  month,
  year,
  onSaved,
}) => {
  const setToast = useSetRecoilState(toastState);
  const subTextColor = useColorModeValue("gray.500", "gray.400");

  const isEditMode = !!existingConfig;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(payoutConfigValidationSchema),
    mode: "onChange",
    defaultValues: { ...PAYOUT_CONFIG_DEFAULTS },
  });

  const [{ loading: createLoading }, createConfig] = useAxios(
    { method: "POST", url: `/api/stations/payoutConfig/${station?.Id}` },
    { manual: true },
  );

  const [{ loading: updateLoading }, updateConfig] = useAxios(
    {
      method: "PATCH",
      url: `/api/stations/payoutConfig/${existingConfig?.Id}`,
    },
    { manual: true },
  );

  const isLoading = createLoading || updateLoading;

  // When opening, seed the form with existing config (edit) or defaults (create).
  useEffect(() => {
    if (!isOpen) return;
    if (existingConfig) {
      reset(payoutConfigFormValues(existingConfig));
    } else {
      reset({ ...PAYOUT_CONFIG_DEFAULTS });
    }
  }, [isOpen, existingConfig, reset]);

  const handleCreate = async (data) => {
    try {
      const payload = buildPayoutConfigPayload(
        data,
        station?.StationId,
        month,
        year,
      );
      const response = await createConfig({ data: payload });
      if (response.data && response.data.data) {
        setToast({
          isOpen: true,
          title: "Payout configuration created successfully",
          status: "success",
          duration: 5000,
        });
        onSaved && onSaved(response.data.data);
        onClose();
      }
    } catch (error) {
      logger.error("Error creating payout config:", error);
      setToast({
        isOpen: true,
        title: "Error creating payout configuration",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleUpdate = async (data) => {
    try {
      // Month/Year always identify the target period, so they are sent even
      // when unchanged — they scope the update rather than being an edit.
      const payload = { StationId: station?.StationId };
      if (month) payload.Month = parseInt(month);
      if (year) payload.Year = parseInt(year);
      let hasChanges = false;

      PAYOUT_CONFIG_FIELDS.forEach((field) => {
        const newVal = parseFloat(data[field.name]);

        if (existingConfig[field.name] !== newVal) {
          payload[field.name] = newVal;
          hasChanges = true;
        }
      });

      if (!hasChanges) {
        setToast({
          isOpen: true,
          title: "Nothing to update",
          status: "warning",
          duration: 5000,
        });
        onClose();
        return;
      }

      const response = await updateConfig({ data: payload });
      if (response.data && response.data.data) {
        setToast({
          isOpen: true,
          title: "Payout configuration updated successfully",
          status: "success",
          duration: 5000,
        });
        onSaved && onSaved(response.data.data);
        onClose();
      }
    } catch (error) {
      logger.error("Error updating payout config:", error);
      setToast({
        isOpen: true,
        title: "Error updating payout configuration",
        status: "error",
        duration: 5000,
      });
    }
  };

  const onSubmit = (data) => {
    if (isEditMode) {
      handleUpdate(data);
    } else {
      handleCreate(data);
    }
  };

  const headerText = useMemo(
    () =>
      isEditMode
        ? "Edit Charger Payout Configuration"
        : "Create Charger Payout Configuration",
    [isEditMode],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent borderRadius="20px" maxH="90vh">
        <ModalHeader>
          <Flex direction="column" gap={1}>
            <Text fontSize="lg" fontWeight="bold">
              {headerText}
            </Text>
            {station && (
              <Flex align="center" gap={2}>
                <Text fontSize="sm" color={subTextColor}>
                  {station.Name || station.StationId}
                </Text>
                <Badge
                  colorScheme="orange"
                  variant="subtle"
                  fontSize="xs"
                  borderRadius="full"
                  px={2}
                  py={0.5}
                >
                  {station.StationId}
                </Badge>
              </Flex>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <form id="payout-config-form" onSubmit={handleSubmit(onSubmit)}>
              <SimpleGrid
                columns={{ base: 1, sm: 2, lg: 3 }}
                spacing={6}
                maxW="100%"
              >
                {PAYOUT_CONFIG_FIELDS.map((field) => (
                  <Box key={field.name}>
                    <Flex direction="column">
                      <FormLabel htmlFor={field.name}>{field.label}</FormLabel>
                      <Controller
                        name={field.name}
                        control={control}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                          <FormControl isInvalid={errors[field.name]}>
                            <StyledInput
                              id={field.name}
                              type={field.type}
                              step={field.step}
                              placeholder={field.placeholder}
                              onChange={(e) => {
                                const val = e.target.value;
                                onChange(val === "" ? "" : parseFloat(val));
                              }}
                              onBlur={onBlur}
                              value={formatPayoutConfigInputValue(value)}
                              ref={ref}
                            />
                            {errors[field.name] && (
                              <FormErrorMessage>
                                {errors[field.name].message}
                              </FormErrorMessage>
                            )}
                          </FormControl>
                        )}
                      />
                    </Flex>
                  </Box>
                ))}
              </SimpleGrid>
          </form>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            bg="#f57d36"
            color="white"
            _hover={{ bg: "#e06826" }}
            _active={{ bg: "#c95a1f" }}
            type="submit"
            form="payout-config-form"
            isLoading={isLoading}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PayoutConfigModal;
